import { useEffect, useState, useRef } from "react";
import tw from "tailwind-styled-components";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import Config from "../../Config";
import { useSelector, useDispatch } from "react-redux";
import { useMutation } from "react-query";
import { DashboardSkeletonForSalesHead } from "../../Components/SkeletonLoading";
import {
    Select_Property,
    Select_Location,
    Save_Locations,
} from "../../Redux/actions";
import PropertiesFilterDropdown from "../../Components/PropertiesFilterDropdown";
import LocationsFilterDropdown from "../../Components/LocationsFilterDropdown";
import { BoxTitle } from "../../Components/Styles/PageStyles";
import { hasPermission } from "../../helpers";
import KPIs from "../../Components/Complaint/Dashboard/KPIs";
import ComplaintAnalytics from "../../Components/Complaint/Dashboard/ComplaintAnalytics";

const Dashboard = () => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.UserReducer.user);

    const property = useSelector(
        (state) => state.SelectPropertyReducer.selectProperty
    );
    // const selectedPartner = useSelector(
    //     (state) => state.SelectedPartnerReducer.selectPartner
    // );

    const canViewFinance = hasPermission("VIEW_FINANCIAL_SUMMARY", user);

    const defaultLocations = {
        id: 0,
        name: "All Locations",
        clientId: 0,
    };

    // ---- Dashboard data state ----
    const [apiData, setApiData] = useState();
    const [complaintSummary, setComplaintSummary] = useState();
    const [complaintsCategoryData, setComplaintsCategoryData] = useState([]);
    const [complaintsByProperty, setComplaintsByProperty] = useState([]);
    const [complaintsByTenant, setComplaintsByTenant] = useState([]);
    const [complaintsByAge, setComplaintsByAge] = useState([]);
    const [complaintsByTeam, setComplaintsByTeam] = useState([]);

    const lastFetchRef = useRef(null);

    // ---- Filters ----
    // const [startDate, setStartDate] = useState(
    //     moment().startOf("month").format("YYYY-MM-DD")
    // );
    // const [endDate, setEndDate] = useState(
    //     moment().endOf("month").format("YYYY-MM-DD")
    // );
    const [selectedProperty, setSelectedProperty] = useState(property);
    const [selectedLocation, setSelectedLocation] = useState(defaultLocations);

    const properties = useSelector((state) => state.PropertyReducer.property);
    const locations = useSelector((state) => state.LocationsReducer.locations);

    const [appliedFilters, setAppliedFilters] = useState(() => ({
        // startDate: moment().startOf("month").format("YYYY-MM-DD"),
        // endDate: moment().endOf("month").format("YYYY-MM-DD"),
        propertyId: property?.id ?? 0,
        locationId: defaultLocations.id,
    }));

    // ---- Filtered properties based on location ----
    const filteredProperties =
        selectedLocation?.id === 0
            ? properties
            : [
                properties[0],
                ...properties.filter(
                    (p) =>
                        p.locationId === selectedLocation.id ||
                        p.location?.id === selectedLocation.id
                ),
            ];

    // ---- Sync selected property when location changes ----
    useEffect(() => {
        if (!selectedLocation || !properties?.length) return;

        if (selectedLocation?.id === 0) {
            setSelectedProperty(properties[0]);
            dispatch(Select_Property(properties[0]));

            if (locations && locations.length > 0) {
                setSelectedLocation(locations[0]);
                dispatch(Select_Location(locations[0]));
            }
            return;
        }

        const isPropertyValid = properties.some(
            (p) =>
                p.id === selectedProperty?.id &&
                (p.locationId === selectedLocation.id ||
                    p.location?.id === selectedLocation.id)
        );

        if (!isPropertyValid) {
            setSelectedProperty(properties[0]);
            dispatch(Select_Property(properties[0]));
        }
    }, [selectedLocation, properties]);

    // ---- Apply filters ----
    const onApplyFilters = () => {
        dispatch(Select_Property(selectedProperty));
        dispatch(Select_Location(selectedLocation));
        setAppliedFilters({
            // startDate,
            // endDate,
            propertyId: selectedProperty?.id ?? 0,
            locationId: selectedLocation?.id ?? 0,
        });
    };

    const canApply =
        !appliedFilters ||
        // appliedFilters.startDate !== startDate ||
        // appliedFilters.endDate !== endDate ||
        appliedFilters.propertyId !== selectedProperty?.id ||
        appliedFilters.locationId !== selectedLocation?.id;

    // ---- Query builder ----
    const buildQuery = () => {
        const params = new URLSearchParams();
        // if (startDate) params.append("startDate", startDate);
        // if (endDate) params.append("endDate", endDate);
        if (selectedLocation?.id) params.append("locationId", selectedLocation.id);
        if (selectedProperty?.id) params.append("propId", selectedProperty.id);
        return params.toString();
    };

    // ---- Fetch ----
    const fetchFunction = async () => {
        const query = buildQuery();
        const response = await axios.get(
            `${Config.apiUrl}/complaint/web/dashboard?${query}`,
            {
                headers: {
                    authorization: "Bearer" + " " + user.token,
                    "Content-Type":
                        "application/x-www-form-urlencoded; charset=UTF-8",
                },
            }
        );
        return response.data;
    };

    // ---- Success handler ----
    const getSuccess = (res) => {
        if (res.isSuccess === false) return;

        setApiData(res?.data);
        if (res?.locations) {
            dispatch(Save_Locations([defaultLocations, ...res.locations]));
        }
        // ---- Complaint dashboard data (inside res.data) ----
        setComplaintSummary(res?.data?.summary);
        setComplaintsCategoryData(res?.data?.complaintsCategoryData || []);
        setComplaintsByProperty(res?.data?.complainDataByProp || []);
        setComplaintsByTenant(res?.data?.complainDataByTenant || []);
        setComplaintsByAge(res?.data?.complainDataByAge || []);
        setComplaintsByTeam(res?.data?.complainTeamData || []);
    };

    const getError = (err) => {
        console.error("Complaint dashboard fetch error:", err);
    };

    const {
        isLoading,
        mutate: getListMutate,
        error,
        isRefetching,
    } = useMutation(fetchFunction, {
        onSuccess: getSuccess,
        onError: getError,
    });

    // ---- Reset filters when partner changes ----
    // useEffect(() => {
    //     if (!selectedPartner?.clientId) return;

    //     setAppliedFilters({
    //         // startDate: moment().startOf("month").format("YYYY-MM-DD"),
    //         // endDate: moment().endOf("month").format("YYYY-MM-DD"),
    //         propertyId: 0,
    //         locationId: 0,
    //     });
    // }, [selectedPartner?.clientId]);

    // ---- Trigger fetch when filters change ----
    useEffect(() => {
        if (!appliedFilters || !user?.token) return;

        const signature = JSON.stringify({
            token: user.token,
            filters: appliedFilters,
        });

        if (lastFetchRef.current === signature) return;

        lastFetchRef.current = signature;
        getListMutate();
    }, [appliedFilters, user?.token]);

    // ---- Handlers ----
    const handlePropertySelect = (property) => setSelectedProperty(property);
    const handleLocationSelect = (location) => setSelectedLocation(location);

    return (
        <Container>
            <DashboardHeader title="Dashboard">
                <PropertiesFilterDropdown
                    properties={filteredProperties}
                    selectedProperty={selectedProperty}
                    onPropertySelect={handlePropertySelect}
                    placeholder="All Properties"
                    className="w-full md:w-[200px]"
                />

                <LocationsFilterDropdown
                    locations={locations}
                    selectedLocation={selectedLocation}
                    onSelect={handleLocationSelect}
                    placeholder="All Locations"
                    className="w-full md:w-[200px]"
                />

                <ApplyButton
                    disabled={!canApply}
                    type="button"
                    onClick={onApplyFilters}
                    className="md:ml-2 self-center md:self-auto"
                >
                    Apply
                </ApplyButton>
            </DashboardHeader>

            {(isLoading || isRefetching) && <DashboardSkeletonForSalesHead />}

            {!error && !isLoading && apiData && (
                <>
                    <KPIs
                        data={complaintSummary}
                        setIsShowAnalytics={setIsShowAnalyticsStub}
                    />

                    <ComplaintAnalytics
                        summary={complaintSummary}
                        categoryData={complaintsCategoryData}
                        propertyData={complaintsByProperty}
                        tenantData={complaintsByTenant}
                        ageData={complaintsByAge}
                        teamData={complaintsByTeam}
                    />
                </>
            )}
        </Container>
    );
};

// Placeholder to avoid unused setter warnings (kept for KPI prop compatibility)
const setIsShowAnalyticsStub = () => { };

// ---- Layout components ----
const DashboardHeader = ({ title = "Dashboard", children }) => (
    <Card>
        <Wrapper>
            <TitleWrapper>
                <BoxTitle className="!mb-0 whitespace-nowrap">{title}</BoxTitle>
            </TitleWrapper>
            <ActionsWrapper>{children}</ActionsWrapper>
        </Wrapper>
    </Card>
);

const Container = tw.div`Container`;

const ApplyButton = tw.button`
  px-5 py-2
  rounded-md
  text-sm font-medium
  transition
  shadow-sm

  ${({ disabled }) =>
        disabled
            ? `
        bg-gray-200
        text-gray-400
        cursor-not-allowed
      `
            : `
        bg-gradient-to-r from-primary-color to-[#00A0FF] hover:brightness-110
        text-white
      `}
`;

const Card = tw.div`mt-2 md:mt-7 bg-white rounded-lg shadow-sm`;
const Wrapper = tw.div`flex flex-col lg:flex-row lg:items-center gap-4 px-4 py-3 md:px-6`;
const TitleWrapper = tw.div`shrink-0`;
const ActionsWrapper = tw.div`flex flex-col md:flex-row md:items-center gap-3 flex-1 justify-end p-2`;

export default Dashboard;